import { useQuery } from '@tanstack/react-query'
import { usePublicClient } from 'wagmi'
import { kycRegistryAbi } from '@/abi/kycRegistryAbi'
import { getContractAddress } from '@/config/contracts'
import { matchesEvent } from '@/lib/eventStream'
import { isHexAddress } from '@/lib/utils'
import { chainQueryKeys } from './_chainQueryKeys'
import { streamStaleTime, useStreamInvalidation } from './_streamInvalidation'

export interface KycPlayerGatesRecord {
    // 256-bit gate bitmap. Bits 0-13 map to GATE_TYPES in src/types/player-detail.ts.
    gates: bigint
}

export function useKycPlayerGates(chainId: number, address: string | undefined) {
    const registry = getContractAddress(chainId, 'kycRegistry')
    const validAddress =
        address && isHexAddress(address) ? (address.toLowerCase() as `0x${string}`) : undefined
    const enabled = Boolean(registry) && Boolean(validAddress)
    const queryKey = chainQueryKeys.kycPlayerGates(chainId, validAddress ?? '')
    const publicClient = usePublicClient({ chainId })

    const { connected } = useStreamInvalidation(
        chainId,
        enabled,
        (m) => matchesEvent(m, 'kycRegistry', 'PlayerGatesSet', { player: validAddress }),
        [queryKey],
    )

    const query = useQuery<KycPlayerGatesRecord>({
        queryKey,
        enabled: enabled && Boolean(publicClient),
        staleTime: streamStaleTime(connected),
        queryFn: async () => {
            if (!publicClient || !registry || !validAddress) {
                throw new Error('kyc player gates read attempted before prerequisites resolved')
            }
            const gates = await publicClient.readContract({
                address: registry,
                abi: kycRegistryAbi,
                functionName: 'getPlayerGates',
                args: [validAddress],
            })
            return { gates: gates as bigint }
        },
    })

    return {
        data: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
    }
}
